import type { AnomalyTrendSpec, RawAnomalyDto } from '../models/anomaly.types'

const DAY_MS = 86_400_000
const PAST_BUCKETS = 7
const FUTURE_BUCKETS = 3
export const ANOMALY_INDEX = PAST_BUCKETS

export interface BuildAnomalyTrendSpecInput {
  details: RawAnomalyDto['details']
  metricIndex: number
  metricField: string
  startDate: string
  currentValue: number
  expectedRange: [number, number]
}

function utcDay(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/u.exec(value)
  if (!match) return null
  const [, year, month, day] = match
  if (!year || !month || !day) return null
  const epoch = Date.UTC(Number(year), Number(month) - 1, Number(day))
  const parsed = new Date(epoch)
  if (
    parsed.getUTCFullYear() !== Number(year) ||
    parsed.getUTCMonth() !== Number(month) - 1 ||
    parsed.getUTCDate() !== Number(day)
  ) {
    return null
  }
  return epoch
}

function isoDay(epoch: number): string {
  return new Date(epoch).toISOString().slice(0, 10)
}

function point(value: number | null | undefined): number | null {
  if (value === null || value === undefined) return null
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

function pastPoints(details: RawAnomalyDto['details'], metricIndex: number): (number | null)[] {
  const source = details.last_7_days?.[metricIndex] ?? []
  const normalized = source.slice(-PAST_BUCKETS).map(point)
  return [...Array<null>(Math.max(0, PAST_BUCKETS - normalized.length)).fill(null), ...normalized]
}

/** Build the stable 7-past + anomaly + 3-forecast chart domain. */
export function buildAnomalyTrendSpec(input: BuildAnomalyTrendSpecInput): AnomalyTrendSpec | null {
  const anchor = utcDay(input.startDate)
  if (anchor === null) return null

  const previousDates = Array.from({ length: PAST_BUCKETS }, (_, index) =>
    isoDay(anchor - (PAST_BUCKETS - index) * DAY_MS),
  )
  const datedFuture = input.details.next_3_days_dated?.[input.metricIndex] ?? []
  const rawFuture = input.details.next_3_days?.[input.metricIndex] ?? []
  const futureDates: string[] = []
  const futureValues: (number | null)[] = []
  let cursor = anchor

  for (let index = 0; index < FUTURE_BUCKETS; index += 1) {
    const datedPoint = datedFuture[index]
    const suppliedDate = datedPoint ? utcDay(datedPoint.date) : null
    cursor = suppliedDate ?? cursor + DAY_MS
    futureDates.push(isoDay(cursor))
    futureValues.push(datedPoint ? point(datedPoint.value) : point(rawFuture[index]))
  }

  const expectedBand = Array.from(
    { length: PAST_BUCKETS + 1 + FUTURE_BUCKETS },
    (): [number, number] => [...input.expectedRange],
  )

  return {
    categories: [...previousDates, isoDay(anchor), ...futureDates],
    actual: [...pastPoints(input.details, input.metricIndex), input.currentValue, null, null, null],
    forecast: [...Array<null>(PAST_BUCKETS).fill(null), input.currentValue, ...futureValues],
    expectedBand,
    anomalyIndex: ANOMALY_INDEX,
    metricField: input.metricField,
  }
}
